// Swipeable.jsx
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

export const DIRECTION_LEFT = 'left';
export const DIRECTION_RIGHT = 'right';
export const DIRECTION_UP = 'up';
export const DIRECTION_DOWN = 'down';

export class Swipeable extends React.PureComponent {
  state = {
    startX: 0,
    endX: 0,
    startY: 0,
    endY: 0,
    startTime: 0,
    isStarted: false, // start mouseDown or touchStart
    isDragging: false,
  };
  element = null;
  /**
   * @typedef Velocity
   * @property {number} velocityX - axis x's velocity per milli second.
   * @property {number} velocityY - axis y's velocity per milli second.
   */

  /**
   * @param {number} {deltaX = 0} - axis x's delta value.
   * @param {number} {deltaY = 0} - axis y's delta value.
   * @param {number} {deltaTime = 0} - timestamp delta value.
   * @return {Velocity} axis x and axis y velocity.
   */
  getVelocity = ({ deltaX = 0, deltaY = 0, deltaTime = 0 }) => ({
    velocityX: Math.abs(deltaX) / deltaTime,
    velocityY: Math.abs(deltaY) / deltaTime,
  });
  /**
   * @param {number} {deltaX = 0} - axis x's delta value.
   * @param {number} {deltaY = 0} - axis y's delta value.
   * @return {'up'|'down'|'left'|'right'} swipe direction.
   */
  getDirection = ({ deltaX = 0, deltaY = 0 }) => {
    const isHorizontalSwipe = Math.abs(deltaX) >= Math.abs(deltaY);

    if (isHorizontalSwipe) {
      const isSwipeLeft = deltaX < 0;

      return isSwipeLeft ? DIRECTION_LEFT : DIRECTION_RIGHT;
    }

    const isSwipeDown = deltaY < 0;

    return isSwipeDown ? DIRECTION_DOWN : DIRECTION_UP;
  };
  handleMouseDown = event => {
    this.start({ x: event.pageX, y: event.pageY });
  };
  handleMouseMove = event => {
    this.move({ x: event.pageX, y: event.pageY });
  };
  handleMouseUp = event => {
    this.end({ x: event.pageX, y: event.pageY });
  };
  handleTouchStart = event => {
    const [touch] = event.changedTouches;
    this.start({ x: touch.pageX, y: touch.pageY });
  };
  handleTouchMove = event => {
    const { isDragging } = this.state;

    if (isDragging) {
      event.preventDefault();
    }

    const [touch] = event.changedTouches;

    this.move({ x: touch.pageX, y: touch.pageY });
  };
  handleTouchEnd = event => {
    const [touch] = event.changedTouches;

    this.end({ x: touch.pageX, y: touch.pageY });
  };
  start = ({ x, y }) => {
    this.setState({
      startX: x,
      endX: x,
      startY: y,
      endY: y,
      isStarted: true,
    });
  };
  move = ({ x: endX, y: endY }) => {
    const { startX, startY, startTime, isStarted, isDragging } = this.state;

    if (!isStarted) {
      return;
    }

    const deltaX = endX - startX;
    const deltaY = endY - startY;

    if (!isDragging) {
      if (Math.abs(deltaX) >= 10) {
        this.setState({
          isDragging: true,
          startTime: Date.now(),
        });

        const { onSwipeStart } = this.props;
        return onSwipeStart({ startX, startY });
      }

      return;
    }

    this.setState({
      endX,
      endY,
    });

    const deltaTime = Date.now() - startTime;
    const { velocityX, velocityY } = this.getVelocity({
      deltaX,
      deltaY,
      deltaTime,
    });
    const direction = this.getDirection({ deltaX });

    const { onSwiping } = this.props;

    return onSwiping({
      deltaX,
      deltaY,
      velocityX,
      velocityY,
      direction,
    });
  };
  end = () => {
    const { isDragging, startX, startY, endX, endY, startTime } = this.state;

    this.setState({
      isStarted: false,
    });

    if (!isDragging) {
      return;
    }

    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const deltaTime = Date.now() - startTime;
    const { velocityX, velocityY } = this.getVelocity({
      deltaX,
      deltaY,
      deltaTime,
    });
    const direction = this.getDirection({ deltaX });

    this.setState({
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0,
      startTime: 0,
      isDragging: false,
    });

    const { onSwipeEnd } = this.props;

    return onSwipeEnd({
      deltaX,
      deltaY,
      velocityX,
      velocityY,
      direction,
    });
  };
  setRef = element => {
    this.element = element;
  };
  componentDidMount() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.addEventListener('touchmove', this.handleTouchMove, {
      passive: false,
    });
    document.addEventListener('touchend', this.handleTouchEnd);

    if (this.element) {
      this.element.addEventListener('mousedown', this.handleMouseDown);
      this.element.addEventListener('touchstart', this.handleTouchStart);
    }
  }
  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.removeEventListener('touchmove', this.handleTouchMove);
    document.removeEventListener('touchend', this.handleTouchEnd);

    if (this.element) {
      this.element.removeEventListener('mousedown', this.handleMouseDown);
      this.element.removeEventListener('touchstart', this.handleTouchStart);
    }
  }
  render() {
    const { children } = this.props;

    return <StyledSwipeable ref={this.setRef}>{children}</StyledSwipeable>;
  }
}

Swipeable.propTypes = {
  onSwipeStart: PropTypes.func,
  onSwiping: PropTypes.func,
  onSwipeEnd: PropTypes.func,
  children: PropTypes.element.isRequired,
};

Swipeable.defaultProps = {
  onSwipeStart: () => null,
  onSwiping: () => null,
  onSwiped: () => null,
};

const StyledSwipeable = styled.div`
  width: 100%;
`;

export default Swipeable;
